// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { DataTablePresentationProps } from "@awc/MobiusOverrides/DataTable/DataTable";
import { FormFieldClickable } from "@awc/MobiusOverrides/FormField";
import { OverflowMenuPresentationProps } from "@awc/MobiusOverrides/OverflowMenu";
import { TextFieldPresentationProps } from "@awc/MobiusOverrides/TextField";
import Search from "@awc/XDL/Icons/Search";
// import { ProductDescriptionStyles } from "@awc/Overrides/Components/ProductDescription";
import { fontConfig } from "@awc/XDL/PreStyle/typography";
import { xBaseTheme } from "@awc/XDL/types";
import { ellipsis, styledScrollbar, xScreen } from "@awc/XDL/Utilities";
import color from "@awc/XDL/Utilities/color";
import { mergeWithCss } from "@awc/XDL/Utilities/mergeWithCss";
import { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { ProductTextBadgesStyles } from "@insite/content-library/Components/ProductTextBadges";
import { AccordionSectionHeaderProps } from "@insite/mobius/AccordionSection/AccordionSectionHeader";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import DataTableCellBase from "@insite/mobius/DataTable/DataTableCellBase";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconWrapper } from "@insite/mobius/Icon";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import { LinkPresentationProps, StyledClickable, StyledTypography } from "@insite/mobius/Link";
import { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { resolveColor } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";

export const styledLinksMenu = (width: number) => {
    const xColor = {
        background: {
            hover: "common.ui5",
        },
        text: {
            default: "text.main",
            hover: "common.ui1",
        },
    };

    return ({ theme }: any) => css`
        width: ${width}px;
        display: flex;
        flex-direction: column;
        max-height: 190px;
        border-radius: 4px;
        ${StyledClickable} {
            display: flex;
            width: 100%;
            ${/* sc-selector */ StyledTypography} {
                font-size: ${fontConfig.fontSize.small}px;
                line-height: ${fontConfig.lineHeight.singleLineSmall};
                padding: 4px 12px;
                color: ${resolveColor(xColor.text.default, theme)};
                font-weight: ${fontConfig.weight.regular};
                text-transform: none;
                text-align: left;
                letter-spacing: 0;
                width: 100%;
            }
            &:not(:disabled):not([disabled]):hover ${/* sc-selector */ StyledTypography} {
                background: ${resolveColor(xColor.background.hover, theme)};
                color: ${resolveColor(xColor.text.hover, theme)};
            }
        }

        ${styledScrollbar()}
    `;
};

/**
 * Hide input increment/decrement arrows from browser when textField type = number
 * */
export const hideInputNumberArrows = css`
    // Hide Arrows From Input Number
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    /* Firefox */
    &[type="number"] {
        appearance: textfield;
        -moz-appearance: textfield;
    }
`;

export const companyLogoItem: InjectableCss = {
    css: css`
        display: none;

        @media print {
            display: block;
            padding: 0;
            margin: 0 15px;
            width: 100%;
        }
    `,
};

const linkSizeVariants = {
    small: {
        size: 12,
        lineHeight: "14px",
        iconSize: 12,
    },
    medium: {
        size: 14,
        lineHeight: "16px",
        iconSize: 14,
    },
    large: {
        size: 15,
        lineHeight: "18px",
        iconSize: 15,
    },
};

export const createLinkIconStyles = ({
    icon,
    linkStyles = {},
    sizeVariant = "small",
}: {
    icon?: React.ComponentType | string;
    linkStyles?: LinkPresentationProps;
    sizeVariant?: "small" | "medium" | "large";
}) => {
    const marginPosition = linkStyles?.icon?.position === "right" ? "margin-left" : "margin-right";
    return {
        ...mergeWithCss(
            {
                typographyProps: {
                    size: linkSizeVariants[sizeVariant].size,
                    lineHeight: linkSizeVariants[sizeVariant].lineHeight,
                    weight: fontConfig.weight.bold,
                    css: css`
                        letter-spacing: 0;
                    `,
                },
                icon: icon
                    ? {
                          iconProps: {
                              src: icon,
                              size: linkSizeVariants[sizeVariant].iconSize,
                              css: css`
                                  ${marginPosition}: 4px;
                              `,
                          },
                          position: linkStyles?.icon?.position,
                      }
                    : {},
            },
            linkStyles,
        ),
    } as LinkPresentationProps;
};

export const circleListStyle = css`
    &:before {
        content: "";
        display: inline-block;
        width: 8px;
        min-width: 8px;
        max-width: 8px;
        height: 8px;
        min-height: 8px;
        max-height: 8px;
        border-radius: 50%;
        background-color: ${color("common.ui8")};
        margin-top: 8px;
        margin-right: 8px;
    }
`;

export const pageTitleStyle: TypographyProps = {
    variant: "h1",
    size: 32,
    weight: 900,
    css: css`
        line-height: 40px;
        margin-bottom: 20px;
        display: block;
    `,
};

interface ModalButtonsActionStyles {
    buttonsGridItem?: GridItemProps;
    cancelButton?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
}

export const modalButtonsActionStyles: ModalButtonsActionStyles = {
    buttonsGridItem: {
        width: 12,
        css: css`
            display: flex;
            justify-content: end;
            gap: 10px;
        `,
    },
    cancelButton: {
        variant: "secondary",
    },
    submitButton: {
        variant: "primary",
    },
};

export const sharedProductDescriptionStyles = (lines = 2, sm?: number): ProductDescriptionStyles => {
    let smLines = sm;
    if (!smLines) {
        smLines = lines;
    }
    return {
        productDetailLink: {
            typographyProps: {
                variant: "h3",
                size: fontConfig.fontSize.link,
                forwardAs: "p",
                css: css`
                    margin-bottom: 0;
                    ${ellipsis(1)}
                `,
            },
            css: css`
                margin-bottom: 0;
                align-self: baseline;
                width: auto;
            `,
        },
        productDetailDescription: {
            variant: "h3",
            forwardAs: "p",
            size: fontConfig.fontSize.link,
            css: css`
                margin-bottom: 0;
                ${ellipsis(1)}
            `,
        },
    };
};

export const sharedCustomerToolsTableStyles: DataTablePresentationProps = {
    css: css`
        border: solid ${color("primary")} 3px;
        border-radius: 6px;
        width: max-content;
        border-collapse: collapse;
        -moz-border-radius: 6px;
        display: inline-block;
        vertical-align: top;

        tr {
            border: none;
        }
        tr:nth-of-type(odd) {
            background-color: ${color("common.ui15")};
        }
        tr:nth-of-type(even) {
            background-color: ${color("common.white")};
        }

        &&& td {
            border: none;
        }

        &&& th {
            border: none;
            background: ${color("primary")};
            span {
                color: ${color("common.white")};
            }
        }

        ${DataTableCellBase} {
            &:last-child {
                text-align: left;
            }
            ${xScreen("lte", "sm")} {
                &:first-child {
                    position: relative;
                }
            }
        }
    `,
};

interface MoreActionsSharedStyle {
    overflowMenu?: OverflowMenuPresentationProps;
    moreActionsLink?: LinkPresentationProps;
}
export const moreActionsSharedStyle: MoreActionsSharedStyle = {
    overflowMenu: {
        _width: 140,
        cssOverrides: {
            wrapper: css`
                width: auto;
                height: auto;
                display: flex;
                justify-content: flex-end;
                & button[aria-expanded="true"] ${IconWrapper} svg {
                    transform: rotate(180deg);
                }
            `,
            menu: css`
                padding: 4px 0;
                li:last-child button {
                    padding-bottom: 8px;
                    &::after {
                        display: none;
                    }
                }
            `,

            menuItem: css`
                padding: 8px 12px 0;
                font-size: 12px;
                line-height: 17px;
                color: ${color("text.main")};
                font-weight: bold;
                &::after {
                    content: "";
                    display: block;
                    border-bottom: 1px solid ${color("common.border")};
                    width: 100%;
                    margin-top: 8px;
                }
            `,
        },
    },
    moreActionsLink: {
        icon: {
            iconProps: {
                src: ChevronDown,
                size: 14,
            },
            position: "right",
        },
    },
};

export const getInputStyleWithIcon = (iconSrc = Search): TextFieldPresentationProps => {
    return {
        iconProps: {
            src: iconSrc,
            size: 20,
        },
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
            formInputWrapper: css`
                &&&& > input {
                    padding-left: 38px;
                }
                ${IconWrapper} {
                    background: transparent;
                }
                .clickable-wrapper {
                    left: 2px;
                    right: unset;

                    & > ${FormFieldClickable} {
                        cursor: auto;
                    }
                }
            `,
            inputSelect: css`
                &,
                &:disabled {
                    background: ${color("common.ui16")};
                    border: 0;
                }
            `,
        },
    };
};
export const styledCardStyle = css`
    position: relative;
    padding: 18px 12px 12px;
    border: 1px solid ${color("common.border")};
    &:before {
        content: "";
        background: ${color("secondary")};
        width: 100%;
        height: 6px;
        position: absolute;
        left: 0px;
        top: -1px;
    }
`;

export const boxSharedStyle = ({ variant = "secondary" }: { variant?: "secondary" | "primary" }) => css`
    ${variant === "secondary"
        ? css`
              border: 1px solid ${color("common.border")};
              border-top: 6px solid ${color("secondary")};
          `
        : css`
              border-top: 6px solid ${color("primary")};
              background-color: ${color("common.ui15")};
          `}
    padding: 12px;
`;

export const AuAccordion = {
    mergeCss: true,
    titleTypographyProps: {
        weight: fontConfig.weight.regular,
        size: fontConfig.fontSize.singleLineRegular,
        css: css`
            letter-spacing: 0;
            line-height: ${fontConfig.lineHeight.singleLineRegular};
            margin-bottom: 0;
        `,
    },
    toggleIconProps: {
        css: css`
            display: none;
        `,
    },
    expandIconProps: {
        css: css`
            display: none;
        `,
    },
    headerProps: {
        css: css`
            background: transparent;
            padding: 20px 12px 12px;
            border: 0;
            &:first-of-type {
                border-top: 1px solid ${color("common.border")};
            }
            &&& button {
                background: transparent;
                padding: 0;
                border: none;
                ${TypographyStyle} {
                    color: ${color("secondary")};
                }
            }
            &&& button:focus {
                padding: 0;
            }

            ${({ expanded }: AccordionSectionHeaderProps) =>
                !expanded &&
                css`
                    background: transparent;
                    padding: 20px 12px;
                    &&& button,
                    &&& button:focus {
                        background: transparent;

                        ${TypographyStyle} {
                            color: ${color("secondary")};
                            &:hover {
                                color: ${color("common.ui10")};
                            }
                        }
                    }
                `}
        `,
    },
    panelProps: {
        css: css`
            border: 0;
            border-top: 0;
            padding: 0px 16px;
            margin-bottom: 20px;
        `,
    },
} as xBaseTheme["accordion"]["sectionDefaultProps"];

export const sharedBadgeStyle: ProductTextBadgesStyles & { badgeContainer?: InjectableCss } = {
    badgeContainer: {
        css: css`
            &&&& {
                ${TypographyStyle} {
                    font-size: ${fontConfig.fontSize.xSmall}px;
                    font-weight: ${fontConfig.weight.bold};
                    line-height: ${fontConfig.lineHeight.singleLinexSmall};
                }

                div[class^="LazyImageStyle"] {
                    width: 40px;
                    height: 40px;
                    aspect-ratio: 1;
                }
            }
        `,
    },
    badge: {
        css: css`
            margin-top: 4px;
            display: flex;
            width: 100%;
            justify-content: center;
            padding: 4px 8px;
        `,
    },
    badgeText: {
        size: fontConfig.fontSize.xSmall,
        weight: fontConfig.weight.bold,
        lineHeight: fontConfig.lineHeight.singleLinexSmall,
    },
};

export const barginBinBoxStyle = css`
    border: 4px solid ${color("common.ui32")};
    border-top-width: 0;
    border-bottom-width: 3px;
`;

export const skeletonStyle = css`
    @keyframes shine {
        to {
            background-position-x: -200%;
        }
    }
    background: linear-gradient(
        110deg,
        ${color("common.ui2")} 8%,
        ${color("common.ui1")} 18%,
        ${color("common.ui2")} 33%
    );
    animation: shine 1s linear infinite;
    background-size: 200% 100%;
`;